import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtErr } from "../api";

const ACTIONS = ["view", "create", "edit", "delete", "approve", "manage"];
const MODULE_SUGGESTIONS = ["employees", "departments", "designations", "grades", "shifts", "leaves", "holidays", "attendance", "reports", "users", "roles", "permissions", "master_data", "audit_logs"];

// Admin → Permissions: explicit module grants on top of roles.
// SuperAdmin bypass is automatic (superadmin_bypass) and is never shown as a grant.
const Permissions = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterModule, setFilterModule] = useState("");
  const [form, setForm] = useState({ subject_type: "user", subject_id: "", module: "", action: "view", scope: "all", reason: "" });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = filterModule ? { module: filterModule } : {};
      const [p, u, r] = await Promise.allSettled([
        staffApi.get("/permissions", { params }),
        staffApi.get("/users"),
        staffApi.get("/roles"),
      ]);
      if (p.status === "fulfilled") setRows(Array.isArray(p.value.data) ? p.value.data : []);
      else setError(fmtErr(p.reason));
      if (u.status === "fulfilled") setUsers(Array.isArray(u.value.data) ? u.value.data : []);
      if (r.status === "fulfilled") setRoles(Array.isArray(r.value.data) ? r.value.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterModule]); // eslint-disable-line react-hooks/exhaustive-deps

  const grant = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.subject_id.trim() || !form.module.trim()) {
      setFormError("Subject and module are required.");
      return;
    }
    setBusy(true);
    try {
      await staffApi.post("/permissions", {
        subject_type: form.subject_type,
        subject_id: form.subject_id.trim(),
        module: form.module.trim(),
        action: form.action,
        scope: form.scope || "all",
        reason: form.reason.trim() || null,
      });
      toast.success("Permission granted.");
      setForm({ ...form, subject_id: "", module: "", reason: "" });
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (p) => {
    if (!window.confirm(`Revoke "${p.module}:${p.action}" from ${p.subject_type} ${p.subject_id}?`)) return;
    setDeleting(p.id);
    try {
      await staffApi.delete(`/permissions/${p.id}`);
      toast.success("Permission revoked.");
      await load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setDeleting(null);
    }
  };

  const subjectLabel = (p) => {
    if (p.subject_type === "user") {
      const u = users.find((x) => x.id === p.subject_id);
      return u ? `${u.name} (${u.email || u.employee_code || u.id})` : p.subject_id;
    }
    if (p.subject_type === "role") {
      const r = roles.find((x) => (x.code || x.id) === p.subject_id);
      return r ? (r.name || r.code) : p.subject_id;
    }
    return p.subject_id;
  };

  const inputCls = "w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]";

  return (
    <div data-testid="admin-permissions">
      <h1 className="font-display text-2xl font-bold text-slate-900">Permissions</h1>
      <p className="mt-1 text-sm text-slate-500">Explicit module grants layered on top of roles. The backend enforces these — this screen only manages them.</p>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 p-6 h-fit" data-testid="perm-grant-card">
          <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
            <KeyRound size={14} className="text-[#C5221F]" /> Grant Permission
          </p>
          <form onSubmit={grant} className="mt-5 space-y-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Subject Type</label>
              <select data-testid="perm-subject-type" className={`${inputCls} bg-white`} value={form.subject_type} onChange={(e) => setForm({ ...form, subject_type: e.target.value, subject_id: "" })}>
                <option value="user">User</option>
                <option value="role">Role</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Subject *</label>
              {form.subject_type === "user" ? (
                <select data-testid="perm-subject-user" className={`${inputCls} bg-white`} value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}>
                  <option value="">Select user…</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role}` : ""}</option>)}
                </select>
              ) : (
                <select data-testid="perm-subject-role" className={`${inputCls} bg-white`} value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}>
                  <option value="">Select role…</option>
                  {roles.map((r) => <option key={r.code || r.id || r.name} value={r.code || r.id}>{r.name || r.code}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Module *</label>
              <input data-testid="perm-module" list="perm-module-list" className={inputCls} placeholder="e.g. leaves" value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} />
              <datalist id="perm-module-list">{MODULE_SUGGESTIONS.map((m) => <option key={m} value={m} />)}</datalist>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Action</label>
                <select data-testid="perm-action" className={`${inputCls} bg-white`} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })}>
                  {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Scope</label>
                <select data-testid="perm-scope" className={`${inputCls} bg-white`} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                  <option value="all">all</option>
                  <option value="own">own</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Reason (optional)</label>
              <input data-testid="perm-reason" className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
            {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="perm-form-error">{formError}</p>}
            <button type="submit" disabled={busy} data-testid="perm-grant-btn"
              className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} <Plus size={13} /> Grant
            </button>
          </form>
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Current Grants</p>
            <select value={filterModule} onChange={(e) => setFilterModule(e.target.value)} data-testid="perm-filter-module"
              className="border border-slate-300 px-3 py-2 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]">
              <option value="">All modules</option>
              {MODULE_SUGGESTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {loading ? (
            <p className="mt-8 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
          ) : error ? (
            <p className="mt-4 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="perm-error">{error}</p>
          ) : rows.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="perm-empty">No explicit grants. Access currently comes from roles (SuperAdmin bypasses automatically).</p>
          ) : (
            <div className="mt-4 bg-white border border-slate-200 overflow-x-auto" data-testid="perm-table">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    {["Subject", "Module", "Action", "Scope", "Reason", ""].map((h) => (
                      <th key={h} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((p) => (
                    <tr key={p.id} data-testid={`perm-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{subjectLabel(p)}</p>
                        <p className="text-xs text-slate-400">{p.subject_type}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-medium">{p.module}</td>
                      <td className="px-4 py-3 text-slate-600">{p.action}</td>
                      <td className="px-4 py-3 text-slate-600">{p.scope || "all"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{p.reason || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => revoke(p)} disabled={deleting === p.id} data-testid={`perm-revoke-${p.id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#C5221F] hover:underline disabled:opacity-50">
                          {deleting === p.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Permissions;
